import { Box, Typography, styled } from "@mui/material";
import { DataGridPro, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import moment from "moment-timezone";
import React, { useContext, useMemo } from "react";
import { FlightStatusChip } from "./FlightStatusChip";
import { PairingStatusGridContext } from "./PairingStatusCalendarGrid";
import { FLIGHT_PAIRING_STATUS_CATALOGUE, IFlightPairingStatusRow } from "./pairingStatusTypes";

/**
 * List view: one row per flight, sorted by departure. The quickest way to work through "what needs fixing".
 */

const GridContainer = styled(Box)(() => ({
  width: "100%",
  height: "100%",
  minHeight: 0,
  "& .MuiDataGrid-root": { border: "none", fontSize: 12 },
  "& .MuiDataGrid-cell": { padding: "0px 6px" },
  "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700, fontSize: 11 },
}));

const StatusCell = ({ row }: { row: IFlightPairingStatusRow }): JSX.Element => {
  const { selectedFlightId, highlightedFlightId, onSelect } = useContext(PairingStatusGridContext);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%" }}>
      <FlightStatusChip
        row={row}
        selected={row.flightId === selectedFlightId}
        highlighted={row.flightId === highlightedFlightId}
        onSelect={onSelect}
      />
      <span>{row.status?.label}</span>
    </Box>
  );
};

const positionList = (items: { positionCode: string; count: number }[] | undefined): string =>
  (items ?? []).map((i) => (i.count > 1 ? `${i.positionCode} x${i.count}` : i.positionCode)).join(", ");

const NoRows = ({ message }: { message: string }): JSX.Element => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
    <Typography variant="body2" color="textSecondary">
      {message}
    </Typography>
  </Box>
);

interface IPairingStatusListGridProps {
  rows: IFlightPairingStatusRow[];
  loading: boolean;
  emptyMessage: string;
}

const INITIAL_STATE = { sorting: { sortModel: [{ field: "scheduledOut", sort: "asc" as const }] } };
const getRowId = (row: IFlightPairingStatusRow): string => row.flightId;
/** Tall enough for the 22px status chip plus its selection border. */
const ROW_HEIGHT = 30;

export const PairingStatusListGrid = (props: IPairingStatusListGridProps): JSX.Element => {
  const { rows, loading, emptyMessage } = props;
  const { onSelect } = useContext(PairingStatusGridContext);

  const columns = useMemo<GridColDef<IFlightPairingStatusRow>[]>(
    () => [
      {
        field: "scheduledOut",
        headerName: "Departure (UTC)",
        width: 130,
        valueGetter: (_value, row) => new Date(row.actualOut ?? row.scheduledOut).valueOf(),
        renderCell: (params: GridRenderCellParams<IFlightPairingStatusRow>) =>
          moment.utc(params.row.actualOut ?? params.row.scheduledOut).format("ddd DD MMM HH:mm"),
      },
      {
        field: "flightNumber",
        headerName: "Flight",
        width: 70,
        sortComparator: (a: number, b: number) => a - b,
        valueFormatter: (value, row) => `${value}${row.flightNumberSuffix ?? ""}`,
      },
      { field: "origin", headerName: "Orig.", width: 58 },
      { field: "destination", headerName: "Dest.", width: 58 },
      {
        field: "aircraft",
        headerName: "Aircraft",
        width: 90,
        valueGetter: (_value, row) => row.aircraft?.registration ?? "",
      },
      {
        field: "status",
        headerName: "Status",
        width: 140,
        valueGetter: (_value, row) => FLIGHT_PAIRING_STATUS_CATALOGUE[row.status?.code]?.priority ?? 99,
        renderCell: (params: GridRenderCellParams<IFlightPairingStatusRow>) => <StatusCell row={params.row} />,
      },
      {
        field: "pairings",
        headerName: "Pairings",
        width: 140,
        valueGetter: (_value, row) => (row.pairingShortCodes ?? []).join(", "),
      },
      {
        field: "crew",
        headerName: "Crew",
        width: 70,
        sortable: false,
        valueGetter: (_value, row) => `${row.crewSummary?.assigned ?? 0}/${row.crewSummary?.required ?? 0}`,
      },
      {
        field: "missing",
        headerName: "Missing",
        width: 120,
        sortable: false,
        valueGetter: (_value, row) => positionList(row.crewSummary?.missing),
      },
      {
        field: "extra",
        headerName: "Extra",
        width: 120,
        sortable: false,
        valueGetter: (_value, row) =>
          [positionList(row.crewSummary?.extraCrew), positionList(row.crewSummary?.extraPositions)]
            .filter((s) => s.length > 0)
            .join(", "),
      },
      {
        field: "flightType",
        headerName: "Type",
        width: 110,
        valueGetter: (_value, row) => row.flightType?.title ?? "",
      },
      {
        field: "computedAt",
        headerName: "Evaluated",
        width: 110,
        valueGetter: (_value, row) => new Date(row.computedAt).valueOf(),
        renderCell: (params: GridRenderCellParams<IFlightPairingStatusRow>) =>
          params.row.computedAt ? moment.utc(params.row.computedAt).format("DD MMM HH:mm") : "",
      },
    ],
    []
  );

  const slots = useMemo(() => ({ noRowsOverlay: () => <NoRows message={emptyMessage} /> }), [emptyMessage]);

  return (
    <GridContainer>
      <DataGridPro
        rows={rows}
        columns={columns}
        getRowId={getRowId}
        loading={loading}
        columnHeaderHeight={30}
        rowHeight={ROW_HEIGHT}
        disableRowSelectionOnClick
        disableColumnSelector
        disableDensitySelector
        hideFooter
        initialState={INITIAL_STATE}
        onRowClick={(params) => onSelect(params.row as IFlightPairingStatusRow)}
        slots={slots}
      />
    </GridContainer>
  );
};
